import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { API_BASE } from "@/lib/api";
import { CheckCircle2 } from "lucide-react";

// Preset IDs must match /app/backend/presets.py — sending the ID (not label) lets the
// backend seed the correct thank-you + rebook templates on signup.
const BUSINESS_TYPES = [
  { id: "salon", label: "Salon / Barbershop" },
  { id: "med_spa", label: "Med Spa / Aesthetics" },
  { id: "dental", label: "Dental Practice" },
  { id: "wellness", label: "Wellness / Yoga Studio" },
  { id: "pet_groomer", label: "Pet Groomer" },
  { id: "auto", label: "Auto Detailing / Repair" },
  { id: "consultant", label: "Consultant / Coach" },
  { id: "other", label: "Other" },
];

const TOKEN_KEY = "nv_admin_jwt";

export default function TrialSignupDialog({ open, onOpenChange }) {
  const nav = useNavigate();
  const [form, setForm] = useState({
    business_name: "",
    contact_name: "",
    email: "",
    password: "",
    business_type: "",
    phone: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const handleChange = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async () => {
    if (!form.business_name || !form.contact_name || !form.email) {
      toast.error("Please fill in name, business, and email.");
      return;
    }
    if (form.password.length < 8) {
      toast.error("Password must be at least 8 characters.");
      return;
    }
    setSubmitting(true);
    try {
      const res = await fetch(`${API_BASE}/auth/signup`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(form),
      });
      const data = await res.json();
      if (!res.ok) {
        throw new Error(data.detail || "Signup failed");
      }
      // Log user in immediately — token goes to same key Studio/Admin use.
      localStorage.setItem(TOKEN_KEY, data.access_token);
      setDone(true);
      toast.success("Workspace created — taking you to Studio.");
      setTimeout(() => {
        onOpenChange(false);
        nav("/studio");
      }, 900);
    } catch (e) {
      toast.error(String(e.message || e));
    } finally {
      setSubmitting(false);
    }
  };

  const reset = (v) => {
    onOpenChange(v);
    if (!v) {
      setTimeout(() => {
        setDone(false);
        setForm({ business_name: "", contact_name: "", email: "", password: "", business_type: "", phone: "" });
      }, 200);
    }
  };

  return (
    <Dialog open={open} onOpenChange={reset}>
      <DialogContent className="rounded-3xl bg-[#FAF7F0] border-[#E5E0D8] max-w-lg" data-testid="trial-signup-dialog">
        {!done ? (
          <>
            <DialogHeader>
              <div className="overline mb-2">14-day free trial</div>
              <DialogTitle className="font-heading text-2xl text-[#183D36]">Start turning visits into rebookings.</DialogTitle>
              <DialogDescription className="text-[#4A4A4A]">
                No credit card. Cancel anytime. Setup takes under three minutes.
              </DialogDescription>
            </DialogHeader>
            <div className="grid gap-4 py-2">
              <div className="grid gap-2">
                <Label htmlFor="business_name">Business name</Label>
                <Input data-testid="trial-input-business" id="business_name" value={form.business_name} onChange={handleChange("business_name")} placeholder="Bloom & Balm Salon" />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div className="grid gap-2">
                  <Label htmlFor="contact_name">Your name</Label>
                  <Input data-testid="trial-input-name" id="contact_name" value={form.contact_name} onChange={handleChange("contact_name")} placeholder="Alex Rivera" />
                </div>
                <div className="grid gap-2">
                  <Label htmlFor="phone">Phone (optional)</Label>
                  <Input data-testid="trial-input-phone" id="phone" value={form.phone} onChange={handleChange("phone")} placeholder="(555) 123-4567" />
                </div>
              </div>
              <div className="grid gap-2">
                <Label htmlFor="email">Work email</Label>
                <Input data-testid="trial-input-email" id="email" type="email" value={form.email} onChange={handleChange("email")} placeholder="you@business.com" />
              </div>
              <div className="grid gap-2">
                <Label htmlFor="password">Password</Label>
                <Input data-testid="trial-input-password" id="password" type="password" value={form.password} onChange={handleChange("password")} placeholder="Minimum 8 characters" />
                <span className="text-xs text-[#4A4A4A]">You&apos;ll use this to sign in to your Studio dashboard.</span>
              </div>
              <div className="grid gap-2">
                <Label>Business type</Label>
                <Select value={form.business_type} onValueChange={(v) => setForm({ ...form, business_type: v })}>
                  <SelectTrigger data-testid="trial-input-type" className="rounded-full">
                    <SelectValue placeholder="Pick one — we&apos;ll pre-configure your templates" />
                  </SelectTrigger>
                  <SelectContent>
                    {BUSINESS_TYPES.map((t) => (
                      <SelectItem key={t.id} value={t.id}>{t.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <DialogFooter>
              <Button
                data-testid="trial-submit-btn"
                disabled={submitting}
                onClick={submit}
                className="rounded-full bg-[#183D36] hover:bg-[#122e29] text-[#FAF7F0] w-full sm:w-auto px-8"
              >
                {submitting ? "Creating your workspace…" : "Start free trial"}
              </Button>
            </DialogFooter>
          </>
        ) : (
          <div className="py-6 text-center" data-testid="trial-success">
            <div className="w-16 h-16 rounded-full bg-[#A8BFA3] flex items-center justify-center mx-auto mb-4">
              <CheckCircle2 className="text-[#183D36]" size={30} />
            </div>
            <h3 className="font-heading text-2xl text-[#183D36] mb-2">You&apos;re in.</h3>
            <p className="text-[#4A4A4A]">Taking you to <span className="font-medium text-[#183D36]">/studio</span>…</p>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
